import { motion } from 'framer-motion';

import type {
	Animations,
	BtnStyles,
	CtasObjectModel,
	Decorations,
} from '@/src/Types/Shared/Button';
import {
	ArrowBold,
	ArrowFull,
	ArrowLong,
	ArrowSmall,
	OpenNewTabIcon,
} from '@/src/Views/Shared/Icons';
import { __Front } from '@/src/Views/Shared/lib';

type SpanButtonType = {
	label: string;
	url?: string;
	style: BtnStyles;
	urlOpenNewTab?: boolean;
	urlType: string;
	className?: string;
	urlTitle?: string;
	btnHasIcon?: boolean;
	btnDecorationElement?: Decorations;
	primaryClassname?: string;
	secondaryClassname?: string;
	tertiaryClassname?: string;
	aClassname?: string;
	children?: any;
	animation?: Animations;
	ctasStyle?: CtasObjectModel;
};

export function SpanButton(props: SpanButtonType) {
	const {
		label,
		style = 'primary',
		btnHasIcon = false,
		btnDecorationElement = 'long',
		primaryClassname = '',
		secondaryClassname = '',
		tertiaryClassname = '',
		aClassname = '',
		children,
		animation,
		urlOpenNewTab = false,
		ctasStyle,
	} = props;

	const classes: string[] = [];

	const basicStyle = `group relative text-base inline-block transition-all last:mr-0 w-fit`;

	const paddingPrimary = `${ctasStyle?.primary?.btnRemovePadding ? 'p-0' : 'px-4 py-2 tablet:px-8 tablet:py-3'}`;
	const paddingSecondary = `${ctasStyle?.secondary?.btnRemovePadding ? 'p-0' : 'px-4 py-2 tablet:px-8 tablet:py-3'}`;
	const paddingTertiary = `${ctasStyle?.tertiary?.btnRemovePadding ? 'p-0' : 'px-4 py-2 tablet:px-8 tablet:py-3'}`;

	if (style === 'primary') {
		classes.push(primaryClassname, basicStyle, paddingPrimary);
	}

	if (style === 'secondary') {
		classes.push(secondaryClassname, basicStyle, paddingSecondary);
	}

	if (style === 'tertiary') {
		classes.push(tertiaryClassname, basicStyle, paddingTertiary);
	}

	if (style === 'a') {
		classes.push(
			`${aClassname} ${btnHasIcon && btnDecorationElement === 'underline' ? ' underline underline-offset-4' : ''}`
		);
	}

	let animationClass = '';

	switch (animation) {
		case 'scale':
			/* group-hover:animate-scale */
			animationClass = 'motion-safe:group-hover:animate-scale';
			break;

		case 'slideup':
			animationClass =
				'transition-transform duration-300 ease-[cubic-bezier(0.075, 0.82, 0.165, 1)] motion-safe:group-hover:translate-y-[-10px]';
			break;

		case 'shake':
			animationClass = 'motion-safe:group-hover:animate-shake';
			break;

		default:
			animationClass = '';
			break;
	}

	return (
		<>
			<motion.span
				initial='rest'
				whileHover='hover'
				whileTap='pressed'
				tabIndex={-1}
				className={`${classes.join(' ')} ${animationClass} inline-flex items-center justify-center gap-2`}
			>
				{children ? (
					<>{children}</>
				) : (
					<span
						className={`${btnHasIcon && btnDecorationElement === 'underline' ? 'underline underline-offset-4' : ''}`}
					>
						{label}
					</span>
				)}

				{urlOpenNewTab ? (
					<>
						<span className='sr-only'>&nbsp;-&nbsp;{__Front('front.openNewTab')}</span>
						<span title={__Front('front.openNewTab')} aria-hidden='true'>
							<OpenNewTabIcon />
						</span>
					</>
				) : (
					btnHasIcon && (
						<>
							{btnDecorationElement === 'long' && (
								<ArrowLong
									width='20'
									className='transition-transform group-hover:translate-x-2 group-hover:duration-300 group-hover:ease-in-out'
								/>
							)}

							{btnDecorationElement === 'short' && (
								<ArrowSmall
									width='20'
									className='transition-transform group-hover:translate-x-2 group-hover:duration-300 group-hover:ease-in-out'
								/>
							)}

							{btnDecorationElement === 'bold' && (
								<ArrowBold
									width='20'
									className='transition-transform group-hover:translate-x-2 group-hover:duration-300 group-hover:ease-in-out'
								/>
							)}

							{btnDecorationElement === 'full' && (
								<ArrowFull
									width='20'
									className='transition-transform group-hover:translate-x-2 group-hover:duration-300 group-hover:ease-in-out'
								/>
							)}
						</>
					)
				)}
			</motion.span>
		</>
	);
}
